import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Locale, { genderPair, genderString } from 'data/locale';
import { formatDescription, formatNumber } from 'utils/format';
import Store from 'store';
import { itemPerfection } from 'logic/item';
import { colored } from './builder';
import { computeStats, getStats } from 'logic/stats';
import { useShift } from 'ui/modifiers';

const _L = Locale("bnet-tooltips.js");

const SlotSize = {
  head: "default",
  shoulders: "default",
  neck: "square",
  torso: "big",
  waist: "square",
  hands: "default",
  wrists: "default",
  legs: "default",
  feet: "default",
  finger: "square",
  onehand: "default",
  twohand: "default",
  offhand: "default",
  follower: "square",
  custom: "square",
  customwpn: "square",
  potion: "square",
};

function wrapperClass(ancient) {
  if (ancient === "primal") {
    return "d3-tooltip-item-wrapper d3-tooltip-item-wrapper-PrimalAncientLegendary";
  } else if (ancient) {
    return "d3-tooltip-item-wrapper d3-tooltip-item-wrapper-AncientLegendary";
  } else {
    return "d3-tooltip-item-wrapper";
  }
};

function bodyClass(data, slotSize) {
  const item = Data.itemById[data.id];
  const itemType = Data.itemTypes[item.type];
  let bgEffect = null;
  for (const stat in data.stats) {
    if (Data.stats[stat].elemental) {
      bgEffect = Data.stats[stat].elemental;
      break;
    }
  }
  if (bgEffect && itemType && slotSize[itemType.slot] !== "square") {
    return `tooltip-body effect-bg effect-bg-${bgEffect}`;
  } else if (data.stats.basearmor) {
    let result = "tooltip-body effect-bg effect-bg-armor";
    if (itemType && slotSize[itemType.slot] !== "default") {
      result += ` effect-bg-armor-${slotSize[itemType.slot]}`;
    }
    return result;
  } else {
    return "tooltip-body";
  }
}

const useClass = () => Store.useProfile(profile => profile.class);
const useGender = () => Store.useProfile(profile => profile.gender);

const TooltipBorder = ({ side }) => <div className={`d3-tooltip-item-border d3-tooltip-item-border-${side}`}/>;

function ItemIcon({ id, style={}, ...params }) {
  const charClass = useClass();
  const gender = useGender();
  return <span {...params} style={{backgroundImage: `url(${Data.getItemIcon(id, {class: charClass, gender})})`, ...style}}/>;
}

function ItemTypeRight({ data }) {
  const item = Data.itemById[data.id];
  if (item.type === "potion") {
    return null;
  }
  const itemType = Data.itemTypes[item.type];
  const slotInfo = itemType && (Data.metaSlots[itemType.slot] || Data.itemSlots[itemType.slot]);
  return (
    <ul className="item-type-right">
      {!!slotInfo && <li className="item-slot">{slotInfo.name}</li>}
      {!!(item.class || (itemType && itemType.class)) && <li className="item-class-specific d3-color-white">{Data.classes[item.class || itemType.class].name}</li>}
      {item.rank != null && <li className="item-slot">{`Rank ${item.rank}`}{item.rank === 3 && ' (Max)'}</li>}
    </ul>
  );
}

function ItemType({ data }) {
  const item = Data.itemById[data.id];
  const itemType = Data.itemTypes[item.type];
  if (!itemType && !Data.extraTypes[item.type]) {
    return null;
  }
  let quality = item.quality;
  if ((quality === 'legendary' || quality === 'set') && data.ancient === 'sanctified') {
    quality = 'sanctified';
  }
  const qual = Data.qualities[quality];
  const color = Data.qualities[quality].color;
  const name = genderPair((data.ancient === "primal" && qual.primal || (data.ancient && qual.ancient)) || qual.prefix, itemType ? itemType.name : Data.extraTypes[item.type]); // eslint-disable-line no-mixed-operators
  return (
    <ul className="item-type">
      <li className={`d3-color-${color}`}>{name}</li>
    </ul>
  );
}

function ItemArmor({ data }) {
  let armor, armor_max;
  if (data.stats.basearmor instanceof Array) {
    armor = data.stats.basearmor[0];
  } else {
    armor = (data.stats.basearmor.min || 0);
    armor_max = (data.stats.basearmor.max || 0);
  }
  if (data.stats.armor) {
    if (data.stats.armor instanceof Array) {
      armor += data.stats.armor[0];
    } else {
      armor_max = (armor_max || armor) + (data.stats.armor.max || 0);
      armor += (data.stats.armor.min || 0);
    }
  }
  if (data.gems) {
    for (const gem of data.gems) {
      if (gem?.stats?.armor) {
        armor += gem.stats.armor[0];
      }
    }
  }
  if (armor_max) {
    armor += "\u2013" + armor_max;
  }
  return (
    <ul className="item-armor-weapon item-armor-armor">
      <li className="big">
        <p><span className="value">{armor}</span></p>
      </li>
      <li>{_L`Armor`}</li>
    </ul>
  );
}

function ItemDamage({ data }) {
  const item = Data.itemById[data.id];
  const itemType = Data.itemTypes[item.type];

  let weapon = itemType.weapon;
  if (item.weapon) weapon = Object.assign({}, weapon, item.weapon);
  let speed = weapon.speed;
  let dmgmin = weapon.min;
  let dmgmax = weapon.max;
  let dmgmin_max, dmgmax_max, speed_max;
  let gem_damage = 0, gem_ias = 0;
  if (data.gems) {
    for (const gem of data.gems) {
      if (gem && gem[1] === "ruby") {
        dmgmin += Data.gemColors.ruby.weapon.amount[gem[0]];
        dmgmax += Data.gemColors.ruby.weapon.amount[gem[0]];
      }
      if (gem?.stats) {
        if (gem.stats.wpnphy1) {
          dmgmin += gem.stats.wpnphy1[0];
          dmgmax += gem.stats.wpnphy1[0];
        }
        if (gem.stats.damage) gem_damage += gem.stats.damage[0];
        if (gem.stats.weaponias) gem_ias = gem.stats.weaponias[0];
      }
    }
  }
  for (var stat in data.stats) {
    if (Data.stats[stat].damage) {
      if (data.stats[stat] instanceof Array) {
        dmgmin += data.stats[stat][0];
        dmgmax += data.stats[stat][1];
      } else {
        dmgmin_max = (dmgmin_max || dmgmin) + (data.stats[stat].max || 0);
        dmgmin += (data.stats[stat].min || 0);
        dmgmax_max = (dmgmax_max || dmgmax) + (data.stats[stat].max2 || 0);
        dmgmax += (data.stats[stat].min2 || 0);
      }
    }
  }
  if (data.stats.damage || gem_damage) {
    if (!data.stats.damage || data.stats.damage instanceof Array) {
      const damage = (data.stats.damage?.[0] || 0) + gem_damage;
      dmgmin *= (1 + 0.01 * damage);
      dmgmax *= (1 + 0.01 * damage);
    } else {
      dmgmin_max = (dmgmin_max || dmgmin) * (1 + 0.01 * (data.stats.damage.max || 0));
      dmgmin *= (1 + 0.01 * (data.stats.damage.min || 0));
      dmgmax_max = (dmgmax_max || dmgmax) * (1 + 0.01 * (data.stats.damage.max || 0));
      dmgmax *= (1 + 0.01 * (data.stats.damage.min || 0));
    }
  }
  if (data.stats.weaponias || gem_ias) {
    if (!data.stats.weaponias || data.stats.weaponias instanceof Array) {
      const ias = (data.stats.weaponias?.[0] || 0) + gem_ias;
      speed *= (1 + 0.01 * ias);
    } else {
      speed_max = (speed_max || speed) * (1 + 0.01 * (data.stats.weaponias.max || 0));
      speed *= (1 + 0.01 * (data.stats.weaponias.min || 0));
    }
  }
  let dps = ((dmgmin + dmgmax) * speed / 2).toFixed(1);
  let cls = "big";
  if (dmgmin_max || dmgmax_max || speed_max) {
    dps += "\u2013" + (((dmgmin_max || dmgmin) + (dmgmax_max || dmgmax)) * (speed_max || speed) / 2).toFixed(1);
    cls = "med";
  }
  dmgmin = Math.round(dmgmin);
  dmgmax = Math.round(dmgmax);
  speed = speed.toFixed(2);
  if (speed_max) {
    speed += "\u2013" + speed_max.toFixed(2);
  }

  if (dmgmin_max) {
    dmgmin = `(${dmgmin}\u2013${Math.round(dmgmin_max)})`;
  }
  if (dmgmax_max) {
    dmgmax = `(${dmgmax}\u2013${Math.round(dmgmax_max)})`;
  }
  return (
    <React.Fragment>
      <ul className="item-armor-weapon item-weapon-dps">
        <li className={cls}><span className="value">{dps}</span></li>
        <li>{_L`Damage Per Second`}</li>
      </ul>
      <ul className="item-armor-weapon item-weapon-damage">
        <li><p><span className="value">{dmgmin}</span>&#x2013;<span className="value">{dmgmax}</span> <span className="d3-color-FF888888">{_L`Damage`}</span></p></li>
        <li><p><span className="value">{speed}</span> <span className="d3-color-FF888888">{_L`Attacks per Second`}</span></p></li>
      </ul>
    </React.Fragment>
  );
};

function ItemBlock({ data }) {
  let block, block_max;
  if (data.stats.baseblock instanceof Array) {
    block = data.stats.baseblock[0];
  } else {
    block = (data.stats.baseblock.min || 0);
    block_max = (data.stats.baseblock.max || 0);
  }
  if (data.stats.block) {
    if (data.stats.block instanceof Array) {
      block += data.stats.block[0];
    } else {
      block_max = (block_max || block) + (data.stats.block.max || 0);
      block += (data.stats.block.min || 0);
    }
  }

  block = block.toFixed(1);
  if (block_max) {
    block += "\u2013" + block_max.toFixed(1);
  }
  var blockamount;
  if (data.stats.blockamount instanceof Array) {
    blockamount = <p><span className="value">{data.stats.blockamount[0].toFixed(0)}</span>&#x2013;
      <span className="value">{data.stats.blockamount[1].toFixed(0)}</span>
      {" "}<span className="d3-color-FF888888">{_L`Block Amount`}</span></p>;
  } else {
    blockamount = <p><span className="value">({(data.stats.blockamount.min || 0).toFixed(0)}&#x2013;{(data.stats.blockamount.max || 0).toFixed(0)})</span>&#x2013;
      <span className="value">({(data.stats.blockamount.min2 || 0).toFixed(0)}&#x2013;{(data.stats.blockamount.max2 || 0).toFixed(0)})</span>
      {" "}<span className="d3-color-FF888888">{_L`Block Amount`}</span></p>;
  }
  return (
    <ul className="item-armor-weapon item-armor-shield">
      <li><p><span className="value">+{block}%</span> <span className="d3-color-FF888888">{_L`Chance to Block`}</span></p></li>
      <li>{blockamount}</li>
    </ul>
  );
};

function StatRange({ stat, range, percent }) {
  if (!range || !range.min || !range.max) return null;
  const minval = range.min;
  const maxval = (Data.stats[stat].damage && range.max2) || range.max;
  let mintmp = minval, maxtmp = maxval;
  let decimal = 0;
  while (decimal < 2 && (Math.floor(mintmp - 1e-6) === Math.floor(mintmp + 1e-6) || Math.floor(maxtmp - 1e-6) === Math.floor(maxtmp + 1e-6))) {
    mintmp *= 10;
    maxtmp *= 10;
    decimal += 1;
  }
  return <span className="d3-color-gray d3-tooltip-range"> [{formatNumber(minval, decimal, 10000)} - {formatNumber(maxval, decimal, 10000)}]{percent ? "%" : ""}</span>;
}

function StatPercent({ value, className }) {
  let red = Math.round(255 * (value > 0.5 ? 2 - 2 * value : 1));
  let green = Math.round(255 * (value > 0.5 ? 1 : 2 * value));
  red = Math.max(0, Math.min(255, red));
  green = Math.max(0, Math.min(255, green));
  let text = parseFloat((value * 100).toFixed(1)) + "%";
  if (className) text = ` (${text})`;
  return <span className={className} style={{color: `rgb(${red}, ${green}, 0)`}}>{text}</span>;
}

function ItemStats({ data, secondary, affixes, perfection, merged }) {
  const charClass = useClass();

  const sorted = Object.keys(data.stats).filter(stat => {
    if (stat === "sockets" || Data.stats[stat].base) return false;
    return merged || (!Data.stats[stat].secondary) === (!secondary);
  });
  sorted.sort((a, b) => Data.stats[a].prio - Data.stats[b].prio);
  if (!sorted.length) {
    return null;
  }
  const item = Data.itemById[data.id];
  return (
    <React.Fragment>
      {item.type !== "potion" && !merged && <p className="item-property-category">{secondary ? _L`Secondary` : _L`Primary`}</p>}
      {sorted.map(stat => {
        let propType = "default";
        if (Data.stats[stat].utility) {
          propType = "utility";
        }
        if (data.enchant === stat) {
          propType = "enchant";
        }
        const propColor = Data.stats[stat]?.legendary || merged ? "orange" : "blue";

        let format = Data.stats[stat].format, formatStat = stat;
        if (stat === "custom" && item.required?.custom) {
          if (item.required.custom.options) {
            const custom = Data.itemById[data.customId || item.required.custom.options[0]]?.required?.custom;
            if (custom && Array.isArray(data.stats[stat])) {
              format = custom.format;
              formatStat = custom.id;
            } else {
              format = _L`Random legendary weapon effect`;
            }
          } else {
            format = item.required.custom.format;
            formatStat = item.required.custom.id;
          }
        } else if (stat === "extra_passive" && !Array.isArray(data.stats[stat])) {
          format = _L`Random class passive.`;
        }
        const percent = !!format.match(/%(d|\.[0-9]f)%%/);

        format = Data.formatBonus(charClass, format, data.stats[stat], stat === "custom" ? 2 : 1, formatStat);

        return (
          <li className={`d3-color-${propColor} d3-item-property-${propType}`} key={stat}>
            <p>
              {format}
              {!data.template && <StatRange stat={stat} range={affixes[stat]} percent={percent}/>}
              {!!(perfection && perfection.stats[stat]) && <StatPercent className="d3-perfection-value" value={perfection.stats[stat]}/>}
            </p>
          </li>
        );
      })}
    </React.Fragment>
  );
}

function ItemRandom({ data, affixes }) {
  const charClass = useClass();

  return (
    <React.Fragment>
      <br/>
      {!!data.varies && data.varies.map((list, idx) => (
        <li className="item-effects-choice" key={idx}>
          <span className="d3-color-blue">{_L`One of ${<span className="value">{list.length}</span>} Magic Properties (varies)`}</span>
          <ul>
            {list.map(stat => (
              <li key={stat}>
                <span className="d3-color-blue">
                  <p>{Data.formatBonus(charClass, Data.stats[stat].format, affixes[stat], 1, stat)}</p>
                </span>
              </li>
            ))}
          </ul>
        </li>
      ))}
      {!!data.random && <li className="d3-color-blue"><span className="value">+{data.random}</span> {_L`Random Magic Properties`}</li>}
    </React.Fragment>
  );
}

function ItemSockets({ data }) {
  const item = Data.itemById[data.id];
  const itemType = Data.itemTypes[item.type];
  const slotInfo = (Data.metaSlots[itemType.slot] || Data.itemSlots[itemType.slot]);
  const charClass = useClass();
  const gender = useGender();

  let slotType = slotInfo.socketType;
  if (slotType !== "weapon" && slotType !== "head") {
    slotType = "other";
  }
  const numSockets = (data.stats.sockets.max || data.stats.sockets[0]);
  return (
    <React.Fragment>
      {[...Array(numSockets)].map((dummy, idx) => {
        if (data.gems && idx < data.gems.length) {
          const gem = data.gems[idx];
          if (gem && Data.itemById[gem.id]) {
            const info = Data.itemById[gem.id];
            const icon = Data.getItemIcon(gem.id, {class: charClass, gender});
            const affixes = Data.getItemAffixesById(gem.id, false, true);
            return (
              <li className="full-socket" key={idx}>
                <img className="gem" src={icon} alt={info.name}/>
                <span className="d3-color-red">{info.name}</span>
                {!!info.rank && " "}{!!info.rank && <span className="item-jewel-rank">&#x2013; {_L`Rank ${info.rank}`}{info.rank === 3 && ' ' + _L`(Max)`}</span>}
                <ul>
                  <ItemStats data={gem} secondary={false} affixes={affixes} merged={true}/>
                </ul>
              </li>
            );
          } if (gem && Data.legendaryGems[gem[0]] && Data.legendaryGems[gem[0]].effects) {
            const info = Data.legendaryGems[gem[0]];
            const icon = Data.getItemIcon(gem[0], {class: charClass, gender});
            const value = info.effects[0].value.slice();
            for (let i = 0; i < value.length; ++i) {
              value[i] += gem[1] * info.effects[0].delta[i];
            }
            const effect1 = Data.formatBonus(charClass, info.effects[0].format || Data.stats[info.effects[0].stat].format, value, 2, info.effects[0].stat);
            const effect2 = Data.formatBonus(charClass, info.effects[1].format || Data.stats[info.effects[1].stat].format, info.effects[1].value || [], gem[1] < 25 ? 1 : 2, info.effects[1].stat);
            return (
              <li className="full-socket" key={idx}>
                <img className="gem" src={icon} alt={info.name}/>
                <span className="d3-color-orange">{info.name}</span>
                {!!gem[1] && " "}{!!gem[1] && <span className="item-jewel-rank">&#x2013; {_L`Rank ${gem[1]}`}</span>}
                <ul>
                  <li className="jewel-effect d3-color-orange"><p>{effect1}</p></li>
                  <li className={`jewel-effect d3-color-${gem[1] < 25 ? "gray" : "orange"}`}>
                    <p>{effect2}{gem[1] < 25 && " "}{gem[1] < 25 && colored(_L`(Requires Rank ${25})`, "red")}</p>
                  </li>
                </ul>
              </li>
            );
          } else if (gem && Data.gemColors[gem[1]]) {
            const info = Data.gemColors[gem[1]];
            const icon = Data.getItemIcon(gem, {class: charClass, gender});
            const effect = Data.formatBonus(charClass, Data.stats[info[slotType].stat].format, [info[slotType].amount[gem[0]]], info[slotType].stat);
            return (
              <li className="d3-color-white full-socket" key={idx}>
                <img className="gem" src={icon} alt={`${Data.gemQualities[gem[0]]} ${info.name}`}/>
                <span className="socket-effect">{effect}</span>
              </li>
            );
          } else {
            return <li className="empty-socket d3-color-blue" key={idx}>{_L`Empty Socket`}</li>;
          }
        } else {
          return <li className="empty-socket d3-color-blue" key={idx}>{_L`Empty Socket`}</li>;
        }
      })}
    </React.Fragment>
  );
};

function haveRorg(profile, follower) {
  const ids = ["Unique_Ring_107_x1", "P3_Unique_Ring_107"];
  const items = (follower ? profile.followerItems : profile.items);
  if (items.leftfinger && ids.includes(items.leftfinger.id)) return true;
  if (items.rightfinger && ids.includes(items.rightfinger.id)) return true;
  if (follower) return false;
  if (ids.includes(profile.kanai.weapon)) return true;
  if (ids.includes(profile.kanai.armor)) return true;
  if (ids.includes(profile.kanai.jewelry)) return true;
  if (ids.includes(profile.kanai.extra)) return true;
  return false;
}

function ItemSet({ data, follower }) {
  const profile = Store.useProfile();
  const item = Data.itemById[data.id];

  const set = Data.itemSets[item.set];
  const indexes = {};
  const colors = [];
  let count = 0;
  for (let i = 0; i < set.items.length; ++i) {
   indexes[set.items[i].id] = i;
   colors.push("gray");
  }
  const items = (follower ? profile.followerItems : profile.items);
  for (const s in Data.itemSlots) {
    const sid = items[s]?.id;
    if (sid && indexes[sid] !== undefined) {
      colors[indexes[sid]] = "white";
      count++;
    }
  }
  if (count > 1 && haveRorg(profile, follower)) {
    count++;
  }

  return (
    <ul className="item-itemset">
      <li className="item-itemset-name"><span className="d3-color-green">{set.name}</span></li>
      {set.items.map((it, idx) => {
        const type = Data.itemTypes[it.type];
        const slot = (Data.itemSlots[type.slot] || Data.metaSlots[type.slot]);
        return <li key={it.id} className="item-itemset-piece indent"><span className={`d3-color-${colors[idx]}`}>{it.name} ({slot.name})</span></li>;
      })}
      {Object.entries(set.bonuses).map(([amount, bonuses]) => {
        const bonusColor = (parseInt(amount) <= count ? "green" : "gray");
        return (
          <React.Fragment key={amount}>
            <li className={`d3-color-${bonusColor} item-itemset-bonus-amount`}>{_L`(${<span className="value">{amount}</span>}) Set:`}</li>
            {bonuses.map((bonus, idx) => {
              const effect = Data.formatBonus(follower ? profile.follower : profile.class, bonus.stat ? Data.stats[bonus.stat].format : bonus.format, bonus.value || [], 1, bonus.stat);
              return <li key={idx} className={`d3-color-${bonusColor} item-itemset-bonus-desc indent`}>{effect}</li>;
            })}
          </React.Fragment>
        );
      })}
    </ul>
  );
}

function ItemPerfection({ perfection, compare }) {
  return (
    <React.Fragment>
      <span className="item-unique-equipped">
        {_L`Perfection: `}<StatPercent value={perfection.total}/>{!compare && <span className="d3-perfection-tip"> {_L`(hold shift for details)`}</span>}
      </span>
      {!compare && <span className="d3-perfection-section item-unique-equipped">{perfection.dpsDelta ?
        _L`Damage: ${<StatPercent value={perfection.dps}/>} (max ${colored("+" + formatNumber(perfection.dpsDelta, 0, 10000), "white")})` :
        _L`Damage: ${<StatPercent value={perfection.dps}/>}`
      }</span>}
      {!compare && <span className="d3-perfection-section item-unique-equipped">{perfection.toughnessDelta ?
        _L`Toughness: ${<StatPercent value={perfection.toughness}/>} (max ${colored("+" + formatNumber(perfection.toughnessDelta, 0, 10000), "white")})` :
        _L`Toughness: ${<StatPercent value={perfection.toughness}/>}`
      }</span>}
    </React.Fragment>
  );
}

function ItemComparison({ data, slot }) {
  const shiftKey = useShift();
  const state = Store.useSelector();
  const profile = state.profiles[state.activeProfile];
  let stats;
  if (shiftKey && profile.items[slot] && profile.items[slot].gems && profile.items[slot].gems.some(Boolean)) {
    const item = profile.items[slot];
    stats = computeStats(state, replSlot => {
      if (slot === replSlot) return {...item, gems: []};
    });
  } else {
    stats = getStats(state);
  }
  const altStats = computeStats(state, replSlot => {
    if (slot === replSlot) {
      return shiftKey ? {...data, gems: []} : data;
    }
  });

  return (
    <div className="tooltip-extension">
      <ul className="item-type">
        <li><span className="d3-color-gold">{_L`Stat Changes if Equipped:`}</span></li>
        {Object.values(Data.localeTable.statList).filter(info => info.stat).map(info => {
          const value = stats.getValue(info.stat);
          const altValue = altStats.getValue(info.stat);
          const delta = altValue - value;
          const percent = (value < 0.01 ? (delta === 0 ? 0 : Infinity) : 100 * delta / value);
          return {name: info.shortName || info.name, delta, percent};
        }).sort((a, b) => {
          if (a.percent !== b.percent) return b.percent - a.percent;
          return a.name.localeCompare(b.name);
        }).map(({name, percent, delta}) => (
          <li key={name}>
            <span className="tooltip-icon-bullet"/> {colored(name, "white")}: {colored(isFinite(percent) ? formatNumber(percent, 2) + "%" : "+\u221e", delta === 0 ? "gray" : (delta > 0 ? "green" : "red"))}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default React.memo(function ItemTooltip({ data, slot, compare, follower, soulShard }) {
  const state = Store.useSelector();
  const profile = state.profiles[state.activeProfile];
  const slotSize = {...SlotSize};
  const charClass = follower ? profile.follower : profile.class;
  if (!data) {
    data = (follower ? profile.followerItems[slot] : profile.items[slot]);
    if (!data) return null;
    if (soulShard && data.gems?.[0]?.id) {
      data = data.gems[0];
    }
  }
  if (typeof data === "string") {
    data = {id: data, template: true};
  }

  let icon = data.id;

  if (slot) {
    const types = (slot === "offhand" ? Data.getOffhandTypes(profile, follower) : Data.itemSlots[slot].types);
    for (let type of Object.values(types)) {
      if (type.classes && type.classes.indexOf(charClass) < 0) continue;
      slotSize.custom = slotSize.customwpn = slotSize[type.slot];
      break;
    }
    if (icon === "custom" || icon === "customwpn") {
      for (let type of Object.values(types)) {
        if (type.classes && type.classes.indexOf(charClass) < 0) continue;
        if (icon !== "customwpn" && type.weapon) continue;
        if (icon === "customwpn" && !type.weapon) continue;
        icon = type.generic;
        break;
      }
    }
  }

  const item = Data.itemById[data.id];
  if (!item) {
    return null;
  }

  const affixes = Data.getItemAffixesById(data.id, data.ancient, true);
  if (data.template) {
    data = {...data};
    data.stats = {};
    data.varies = [];
    const required = Data.getItemAffixesById(data.id, data.ancient, "only");
    let remain = Data.getStatCount(data.id);
    remain = remain[0] + remain[1];
    for (const stat in required) {
      data.stats[stat] = required[stat];
      if (!Data.stats[stat].base) {
        --remain;
      }
    }
    const preset = Data.getItemPreset(data.id);
    for (const stat of preset) {
      const list = Data.smartListStats(stat, affixes);
      if (list.length === 1) {
        data.stats[list[0]] = affixes[list[0]];
        --remain;
      } else if (list.length > 1) {
        data.varies.push(list);
        --remain;
      }
    }
    if (data.stats.custom && data.custom) {
      data.stats.custom = data.custom;
    }
    if (data.stats.sockets) {
      data.stats.sockets = Object.assign({}, data.stats.sockets);
      let sox = 3;
      if (item.type !== "chestarmor" && item.type !== "cloak") {
        sox = 1;
      }
      data.stats.sockets.max = Math.min(data.stats.sockets.max, sox);
    }
    data.random = Math.max(0, remain);
  }

  const perfection = !data.template && itemPerfection(state, data, slot);

  const merged = (item.quality === "ethereal" || item.type === "primeshard" || item.type === "lessershard");

  const itemType = Data.itemTypes[item.type];
  const color = Data.qualities[(item.quality === 'legendary' || item.quality === 'set') && data.ancient === 'sanctified' ? 'sanctified' : item.quality].color;
  let titleColor = color;
  if (item.type === 'primeshard' || item.type === 'lessershard') {
    titleColor = 'soulshard';
  }
  const itemName = genderString(item.name);
  return (
    <div className="d3-tooltip d3-tooltip-item">
      <div className={wrapperClass(data.ancient)}>
        <TooltipBorder side="left"/>
        <TooltipBorder side="right"/>
        <TooltipBorder side="top"/>
        <TooltipBorder side="bottom"/>
        <TooltipBorder side="top-left"/>
        <TooltipBorder side="top-right"/>
        <TooltipBorder side="bottom-left"/>
        <TooltipBorder side="bottom-right"/>

        <div className={`tooltip-head tooltip-head-${color}`}>
          <h3 className={classNames(`d3-color-${titleColor}`, {smaller: itemName.length > 25 && itemName.length <= 35, smallest: itemName.length > 35})}>{itemName}</h3>
        </div>

        <div className={bodyClass(data, slotSize)}>
          <span className={`d3-icon d3-icon-item d3-icon-item-large d3-icon-item-${color}`}>
            <span className="icon-item-gradient">
              <ItemIcon className={classNames("icon-item-inner", `icon-item-${item.size || ((itemType && slotSize[itemType.slot]) || "square")}`, {ancient: data.ancient})} id={icon}/>
            </span>
          </span>

          <div className="d3-item-properties">
            <ItemTypeRight data={data}/>
            <ItemType data={data}/>

            {!!data.stats.basearmor && <ItemArmor data={data}/>}
            {!!(itemType && itemType.weapon) && <ItemDamage data={data}/>}
            {!!(data.stats.baseblock && data.stats.blockamount) && <ItemBlock data={data}/>}
            {item.type === "potion" && <div className="item-description d3-color-white"><p>{_L`Instantly restores ${<span className="d3-color-green">60%</span>} Life.`}</p></div>}

            {!!item.description && (
              <div className="item-description d3-color-white">
                <p>{formatDescription(item.description)}</p>
              </div>
            )}

            <div className="item-before-effects"/>

            <ul className="item-effects">
              <ItemStats data={data} secondary={false} affixes={affixes} perfection={!compare && perfection} merged={merged}/>
              {!merged && <ItemStats data={data} secondary={true} affixes={affixes} perfection={!compare && perfection}/>}

              {!!item.stats && item.stats.map((stat, index) => (
                <li className={`d3-color-${stat.color} d3-item-property-${stat.type}`} key={index}>
                  <p>{stat.text}</p>
                </li>
              ))}

              {!!((data.varies && data.varies.length) || data.random) && <ItemRandom data={data} affixes={affixes}/>}
              {!!data.stats.sockets && <ItemSockets data={data}/>}
              {item.type === "potion" && <div className="item-description d3-color-white"><p>{_L`Cooldown: ${30} Sec.`}</p></div>}
            </ul>

            {!!item.set && <ItemSet data={data} follower={follower}/>}

            <ul className="item-extras">
              {!!(itemType || item.type === "potion") && <li className="item-reqlevel"><span className="d3-color-gold">{_L`Required Level: `}</span><span className="value">{itemType ? 70 : 61}</span></li>}
              <li>{_L`Account Bound`}</li>
            </ul>
            {!(perfection && perfection.total != null) && !!itemType && <span className="item-unique-equipped">{_L`Unique Equipped`}</span>}
            {!!(perfection && perfection.total != null) && <ItemPerfection perfection={perfection} compare={compare}/>}
            <span className="clear"/>
          </div>
        </div>

        {!!(compare && compare !== true && !follower) && <ItemComparison data={data} slot={compare}/>}
        {!!item.flavor && !(compare && compare !== true) && <div className="tooltip-extension"><div className="flavor">{item.flavor}</div></div>}
      </div>
    </div>
  );
});

export const SetTooltip = React.memo(function SetTooltip({ id }) {
  const set = Data.itemSets[id];
  const charClass = (set.class || "wizard");

  return (
    <div className="d3-tooltip d3-tooltip-item d3-tooltip-set">
      <div className="d3-tooltip-item-wrapper">
        <div className="d3-item-properties">
          <ul className="item-itemset">
            <li className="item-itemset-name"><span className="d3-color-green">{set.name}</span></li>
            {set.items.map(it => {
              const type = Data.itemTypes[it.type];
              const slot = (Data.itemSlots[type.slot] || Data.metaSlots[type.slot]);
              return <li key={it.id} className="item-itemset-piece indent"><span className="d3-color-white">{it.name} ({slot.name})</span></li>;
            })}
            {Object.entries(set.bonuses).map(([amount, bonuses]) => {
              return (
                <React.Fragment key={amount}>
                  <li className="d3-color-green item-itemset-bonus-amount">{_L`(${<span className="value">{amount}</span>}) Set:`}</li>
                  {bonuses.map((bonus, idx) => {
                    const effect = Data.formatBonus(charClass, bonus.stat ? Data.stats[bonus.stat].format : bonus.format, bonus.value || [], 1, bonus.stat);
                    return <li key={idx} className="d3-color-green item-itemset-bonus-desc indent">{effect}</li>;
                  })}
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
});

export const StatTooltip = React.memo(function StatTooltip({ id }) {
  const stat = Data.stats[id];
  let propType = "default";
  if (stat.utility) {
    propType = "utility";
  }
  const propColor = stat.legendary ? "orange" : "blue";

  const format = Data.formatBonus(stat.class || 'wizard', stat.format, [], 1, id);

  return (
    <div className="d3-tooltip d3-tooltip-item d3-tooltip-set">
      <div className="d3-tooltip-item-wrapper">
        <div className="d3-item-properties">
          <ul className="item-effects">
            <li className={`d3-color-${propColor} d3-item-property-${propType}`} key={stat}>
              <p>{format}</p>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
});
